import React, { useCallback, useRef, useState } from 'react'
import { toast } from 'react-toastify'

import {
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    Divider,
    Grid,
    IconButton,
    MenuItem,
    TextField,
    Tooltip,
    useTheme,
} from '@material-ui/core'
import CategoryIcon from '@material-ui/icons/Category'
import CloseIcon from '@material-ui/icons/Close'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import DescriptionIcon from '@material-ui/icons/Description'
import EditIcon from '@material-ui/icons/Edit'
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile'
import LinkIcon from '@material-ui/icons/Link'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'

import { LoriIcon } from '../../../navigation/layouts/Lori/Icons/LoriIcon'

import { Container, LoadingButton } from '../../../components'
import {
    dismissProductSuggestion,
    formatDocumentTypeLabel,
    getProductProfileLabel,
    ProductDocument,
    ProductDocumentType,
    ProductProfile,
    ProductProfilePatchPayload,
    SuggestedDocument,
} from '../../../models'
import { typographyStyle } from '../../../theme/typographyStyle'

const DOCUMENT_TYPES: ProductDocumentType[] = [
    'manual',
    'warranty',
    'spec_sheet',
    'other',
]

interface Props {
    open: boolean
    profile: ProductProfile | null
    loading?: boolean
    onClose: () => void
    onSaveProfile: (
        profileId: number,
        payload: ProductProfilePatchPayload,
    ) => Promise<ProductProfile>
    onUploadDocument: (
        profileId: number,
        file: File,
        options: { name?: string; document_type: ProductDocumentType },
    ) => Promise<ProductDocument>
    onDeleteDocument: (profileId: number, documentId: number) => Promise<void>
    onDeleteProfile: (profileId: number) => Promise<void>
    onProfileUpdated?: (profile: ProductProfile) => void
}

export const ProductProfileDetailModal: React.FC<Props> = ({
    open,
    profile,
    loading = false,
    onClose,
    onSaveProfile,
    onUploadDocument,
    onDeleteDocument,
    onDeleteProfile,
    onProfileUpdated,
}) => {
    const theme = useTheme()
    const fileInputRef = useRef<HTMLInputElement>(null)

    const [editing, setEditing] = useState(false)
    const [brand, setBrand] = useState('')
    const [modelNumber, setModelNumber] = useState('')
    const [displayName, setDisplayName] = useState('')
    const [documentType, setDocumentType] = useState<ProductDocumentType>(
        'manual',
    )
    const [documentName, setDocumentName] = useState('')
    const [saving, setSaving] = useState(false)
    const [uploading, setUploading] = useState(false)
    const [deletingDocId, setDeletingDocId] = useState<number | null>(null)
    const [deletingProfile, setDeletingProfile] = useState(false)
    const [dismissingId, setDismissingId] = useState<number | null>(null)

    React.useEffect(() => {
        if (!profile) {
            return
        }
        setBrand(profile.brand)
        setModelNumber(profile.model_number)
        setDisplayName(profile.display_name)
        setEditing(false)
    }, [profile, open])

    const handleSaveProfile = async () => {
        if (!profile) {
            return
        }
        setSaving(true)
        try {
            await onSaveProfile(profile.id, {
                brand: brand.trim(),
                model_number: modelNumber.trim(),
                display_name: displayName.trim(),
            })
            toast.success('Product updated')
            setEditing(false)
        } catch (error: any) {
            toast.error(
                error?.response?.data?.detail ?? 'Could not update product',
            )
        } finally {
            setSaving(false)
        }
    }

    const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (!file || !profile) {
            return
        }

        setUploading(true)
        try {
            await onUploadDocument(profile.id, file, {
                name: documentName.trim() || file.name,
                document_type: documentType,
            })
            toast.success('Document uploaded')
            setDocumentName('')
        } catch (error) {
            console.error(error)
            toast.error('Could not upload document')
        } finally {
            setUploading(false)
            if (fileInputRef.current) {
                fileInputRef.current.value = ''
            }
        }
    }

    const handleDeleteDocument = async (documentId: number) => {
        if (!profile) {
            return
        }
        setDeletingDocId(documentId)
        try {
            await onDeleteDocument(profile.id, documentId)
            toast.success('Document deleted')
        } catch (error) {
            console.error(error)
            toast.error('Could not delete document')
        } finally {
            setDeletingDocId(null)
        }
    }

    const handleDeleteProfile = async () => {
        if (!profile) {
            return
        }
        if (
            !window.confirm(
                'Delete this product and all of its documents? Linked inventory assets will remain but lose their product link.',
            )
        ) {
            return
        }

        setDeletingProfile(true)
        try {
            await onDeleteProfile(profile.id)
            toast.success('Product deleted')
            onClose()
        } catch (error) {
            console.error(error)
            toast.error('Could not delete product')
        } finally {
            setDeletingProfile(false)
        }
    }

    const suggestedDocuments = profile?.suggested_documents ?? []

    const handleDismissSuggestion = useCallback(
        async (suggestionId: number) => {
            if (!profile) return

            setDismissingId(suggestionId)
            try {
                await dismissProductSuggestion(profile.id, suggestionId)
                if (onProfileUpdated) {
                    onProfileUpdated({
                        ...profile,
                        suggested_documents: profile.suggested_documents.filter(
                            (d) => d.id !== suggestionId,
                        ),
                    })
                }
            } catch (err) {
                console.error(err)
                toast.error('Failed to dismiss suggestion')
            } finally {
                setDismissingId(null)
            }
        },
        [profile, onProfileUpdated],
    )

    const sectionTitleStyle: React.CSSProperties = {
        ...typographyStyle(theme.typography.subtitle2),
        fontWeight: 700,
        color: theme.palette.text.primary,
        letterSpacing: '0.02em',
    }

    const fieldLabelStyle: React.CSSProperties = {
        fontSize: 11,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: theme.palette.text.disabled,
        marginBottom: 4,
    }

    const fieldValueStyle: React.CSSProperties = {
        fontSize: 15,
        fontWeight: 500,
        color: theme.palette.text.primary,
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{ style: { borderRadius: 16, overflow: 'hidden' } }}
        >
            {/* Header */}
            <Container
                style={{
                    padding: theme.spacing(3, 3, 2.5),
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}08 0%, ${theme.palette.primary.main}02 100%)`,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                }}
            >
                <Container
                    style={{ alignItems: 'center', gap: theme.spacing(1.5) }}
                >
                    <Container
                        style={{
                            width: 44,
                            height: 44,
                            borderRadius: 12,
                            backgroundColor: theme.palette.primary.main,
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <CategoryIcon style={{ fontSize: 24, color: '#fff' }} />
                    </Container>
                    <Container style={{ flexDirection: 'column', gap: 2 }}>
                        <span
                            style={{
                                ...typographyStyle(theme.typography.h6),
                                fontWeight: 700,
                            }}
                        >
                            {profile
                                ? getProductProfileLabel(profile)
                                : 'Product details'}
                        </span>
                        {profile && (
                            <span
                                style={{
                                    ...typographyStyle(
                                        theme.typography.caption,
                                    ),
                                    color: theme.palette.text.secondary,
                                }}
                            >
                                {profile.brand} · {profile.model_number}
                            </span>
                        )}
                    </Container>
                </Container>
                <IconButton size="small" onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </Container>

            <DialogContent style={{ padding: 0 }}>
                {loading || !profile ? (
                    <Container
                        style={{
                            justifyContent: 'center',
                            alignItems: 'center',
                            padding: theme.spacing(8),
                            flexDirection: 'column',
                            gap: theme.spacing(1.5),
                        }}
                    >
                        <CircularProgress size={32} />
                        <span
                            style={{
                                ...typographyStyle(theme.typography.body2),
                                color: theme.palette.text.secondary,
                            }}
                        >
                            Loading product...
                        </span>
                    </Container>
                ) : (
                    <Container
                        style={{
                            flexDirection: 'column',
                            alignItems: 'stretch',
                        }}
                    >
                        {/* Product info section */}
                        <Container
                            style={{
                                flexDirection: 'column',
                                alignItems: 'stretch',
                                gap: theme.spacing(2),
                                padding: theme.spacing(3),
                            }}
                        >
                            <Container
                                style={{
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}
                            >
                                <span style={sectionTitleStyle}>
                                    Product information
                                </span>
                                <Container
                                    style={{
                                        gap: theme.spacing(1),
                                        alignItems: 'center',
                                    }}
                                >
                                    <Chip
                                        size="small"
                                        icon={
                                            <LinkIcon
                                                style={{
                                                    fontSize: 14,
                                                    color:
                                                        theme.palette.success
                                                            .main,
                                                }}
                                            />
                                        }
                                        label={`${profile.asset_count} linked ${
                                            profile.asset_count === 1
                                                ? 'asset'
                                                : 'assets'
                                        }`}
                                        style={{
                                            backgroundColor: `${theme.palette.success.main}10`,
                                            color: theme.palette.success.main,
                                            fontWeight: 600,
                                            fontSize: 12,
                                            height: 26,
                                            border: `1px solid ${theme.palette.success.main}30`,
                                        }}
                                    />
                                    {!editing && (
                                        <IconButton
                                            size="small"
                                            onClick={() => setEditing(true)}
                                            style={{
                                                border: `1px solid ${theme.palette.grey[300]}`,
                                                borderRadius: 8,
                                                padding: 6,
                                            }}
                                        >
                                            <EditIcon
                                                style={{ fontSize: 16 }}
                                            />
                                        </IconButton>
                                    )}
                                </Container>
                            </Container>

                            {editing ? (
                                <Container
                                    style={{
                                        padding: theme.spacing(2.5),
                                        borderRadius: 12,
                                        border: `1px solid ${theme.palette.primary.main}30`,
                                        backgroundColor: `${theme.palette.primary.main}04`,
                                        flexDirection: 'column',
                                        gap: theme.spacing(2),
                                    }}
                                >
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                variant="outlined"
                                                size="small"
                                                label="Brand"
                                                value={brand}
                                                onChange={(event) =>
                                                    setBrand(event.target.value)
                                                }
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                variant="outlined"
                                                size="small"
                                                label="Model number"
                                                value={modelNumber}
                                                onChange={(event) =>
                                                    setModelNumber(
                                                        event.target.value,
                                                    )
                                                }
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth
                                                variant="outlined"
                                                size="small"
                                                label="Display name"
                                                value={displayName}
                                                onChange={(event) =>
                                                    setDisplayName(
                                                        event.target.value,
                                                    )
                                                }
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                    <Container
                                        style={{ gap: theme.spacing(1) }}
                                    >
                                        <LoadingButton
                                            loading={saving}
                                            color="primary"
                                            variant="contained"
                                            onClick={handleSaveProfile}
                                            style={{
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                borderRadius: 8,
                                            }}
                                        >
                                            Save changes
                                        </LoadingButton>
                                        <Button
                                            onClick={() => setEditing(false)}
                                            style={{ textTransform: 'none' }}
                                        >
                                            Cancel
                                        </Button>
                                    </Container>
                                </Container>
                            ) : (
                                <Container
                                    style={{
                                        borderRadius: 12,
                                        border: `1px solid ${theme.palette.grey[200]}`,
                                        overflow: 'hidden',
                                        backgroundColor: theme.palette.grey[50],
                                    }}
                                >
                                    <Container
                                        style={{
                                            flex: 1,
                                            padding: theme.spacing(2, 2.5),
                                            flexDirection: 'column',
                                            borderRight: `1px solid ${theme.palette.grey[200]}`,
                                        }}
                                    >
                                        <span style={fieldLabelStyle}>
                                            Brand
                                        </span>
                                        <span style={fieldValueStyle}>
                                            {profile.brand}
                                        </span>
                                    </Container>
                                    <Container
                                        style={{
                                            flex: 1,
                                            padding: theme.spacing(2, 2.5),
                                            flexDirection: 'column',
                                            borderRight: `1px solid ${theme.palette.grey[200]}`,
                                        }}
                                    >
                                        <span style={fieldLabelStyle}>
                                            Model
                                        </span>
                                        <span style={fieldValueStyle}>
                                            {profile.model_number}
                                        </span>
                                    </Container>
                                    <Container
                                        style={{
                                            flex: 1,
                                            padding: theme.spacing(2, 2.5),
                                            flexDirection: 'column',
                                        }}
                                    >
                                        <span style={fieldLabelStyle}>
                                            Property
                                        </span>
                                        <span style={fieldValueStyle}>
                                            {profile.apartment_name}
                                        </span>
                                    </Container>
                                </Container>
                            )}
                        </Container>

                        <Divider />

                        {/* Documents section */}
                        <Container
                            style={{
                                flexDirection: 'column',
                                alignItems: 'stretch',
                                gap: theme.spacing(2),
                                padding: theme.spacing(3),
                            }}
                        >
                            <Container
                                style={{
                                    alignItems: 'center',
                                    gap: theme.spacing(1),
                                }}
                            >
                                <InsertDriveFileIcon
                                    style={{
                                        fontSize: 18,
                                        color: theme.palette.info.main,
                                    }}
                                />
                                <span style={sectionTitleStyle}>Documents</span>
                                {profile.documents.length > 0 && (
                                    <Chip
                                        size="small"
                                        label={`${profile.documents.length}`}
                                        style={{
                                            height: 20,
                                            fontSize: 11,
                                            fontWeight: 600,
                                            backgroundColor: `${theme.palette.info.main}12`,
                                            color: theme.palette.info.main,
                                        }}
                                    />
                                )}
                            </Container>

                            {profile.documents.length === 0 ? (
                                <Container
                                    style={{
                                        padding: theme.spacing(4, 3),
                                        borderRadius: 12,
                                        border: `2px dashed ${theme.palette.grey[300]}`,
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexDirection: 'column',
                                        gap: theme.spacing(1.5),
                                        backgroundColor: theme.palette.grey[50],
                                    }}
                                >
                                    <DescriptionIcon
                                        style={{
                                            fontSize: 40,
                                            color: theme.palette.grey[400],
                                        }}
                                    />
                                    <span
                                        style={{
                                            ...typographyStyle(
                                                theme.typography.body1,
                                            ),
                                            fontWeight: 500,
                                            color: theme.palette.text.secondary,
                                        }}
                                    >
                                        No documents yet
                                    </span>
                                    <span
                                        style={{
                                            ...typographyStyle(
                                                theme.typography.body2,
                                            ),
                                            color: theme.palette.text.disabled,
                                            textAlign: 'center',
                                            maxWidth: 340,
                                        }}
                                    >
                                        Upload manuals, warranties, or spec
                                        sheets below. They&apos;ll be shared
                                        with all matching assets.
                                    </span>
                                </Container>
                            ) : (
                                <Container
                                    style={{
                                        flexDirection: 'column',
                                        alignItems: 'stretch',
                                        gap: 0,
                                        borderRadius: 12,
                                        border: `1px solid ${theme.palette.grey[200]}`,
                                        overflow: 'hidden',
                                    }}
                                >
                                    {profile.documents.map(
                                        (
                                            document: ProductDocument,
                                            index: number,
                                        ) => (
                                            <Container
                                                key={document.id}
                                                style={{
                                                    justifyContent:
                                                        'space-between',
                                                    alignItems: 'center',
                                                    gap: theme.spacing(2),
                                                    padding: theme.spacing(
                                                        1.75,
                                                        2.5,
                                                    ),
                                                    borderBottom:
                                                        index <
                                                        profile.documents
                                                            .length -
                                                            1
                                                            ? `1px solid ${theme.palette.grey[100]}`
                                                            : 'none',
                                                    backgroundColor:
                                                        theme.palette.background
                                                            .paper,
                                                    transition:
                                                        'background-color 0.15s',
                                                }}
                                            >
                                                <Container
                                                    style={{
                                                        alignItems: 'center',
                                                        gap: theme.spacing(1.5),
                                                        flex: 1,
                                                        minWidth: 0,
                                                    }}
                                                >
                                                    <Container
                                                        style={{
                                                            width: 32,
                                                            height: 32,
                                                            borderRadius: 8,
                                                            backgroundColor:
                                                                theme.palette
                                                                    .grey[100],
                                                            alignItems:
                                                                'center',
                                                            justifyContent:
                                                                'center',
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        <DescriptionIcon
                                                            style={{
                                                                fontSize: 18,
                                                                color:
                                                                    theme
                                                                        .palette
                                                                        .grey[600],
                                                            }}
                                                        />
                                                    </Container>
                                                    <Container
                                                        style={{
                                                            flexDirection:
                                                                'column',
                                                            gap: 1,
                                                            minWidth: 0,
                                                            flex: 1,
                                                        }}
                                                    >
                                                        <span
                                                            style={{
                                                                fontSize: 14,
                                                                fontWeight: 500,
                                                                overflow:
                                                                    'hidden',
                                                                textOverflow:
                                                                    'ellipsis',
                                                                whiteSpace:
                                                                    'nowrap',
                                                            }}
                                                        >
                                                            {document.name}
                                                        </span>
                                                        <span
                                                            style={{
                                                                fontSize: 12,
                                                                color:
                                                                    theme
                                                                        .palette
                                                                        .text
                                                                        .disabled,
                                                            }}
                                                        >
                                                            {formatDocumentTypeLabel(
                                                                document.document_type,
                                                            )}
                                                        </span>
                                                    </Container>
                                                </Container>
                                                <Container
                                                    style={{
                                                        gap: 6,
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    <Tooltip title="Open in new tab">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                window.open(
                                                                    document.file,
                                                                    '_blank',
                                                                )
                                                            }
                                                            style={{
                                                                border: `1px solid ${theme.palette.grey[200]}`,
                                                                borderRadius: 8,
                                                                padding: 6,
                                                            }}
                                                        >
                                                            <OpenInNewIcon
                                                                style={{
                                                                    fontSize: 16,
                                                                }}
                                                            />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete document">
                                                        <IconButton
                                                            size="small"
                                                            disabled={
                                                                deletingDocId ===
                                                                document.id
                                                            }
                                                            onClick={() =>
                                                                handleDeleteDocument(
                                                                    document.id,
                                                                )
                                                            }
                                                            style={{
                                                                border: `1px solid ${theme.palette.grey[200]}`,
                                                                borderRadius: 8,
                                                                padding: 6,
                                                            }}
                                                        >
                                                            {deletingDocId ===
                                                            document.id ? (
                                                                <CircularProgress
                                                                    size={14}
                                                                />
                                                            ) : (
                                                                <DeleteOutlineIcon
                                                                    style={{
                                                                        fontSize: 16,
                                                                        color:
                                                                            theme
                                                                                .palette
                                                                                .error
                                                                                .light,
                                                                    }}
                                                                />
                                                            )}
                                                        </IconButton>
                                                    </Tooltip>
                                                </Container>
                                            </Container>
                                        ),
                                    )}
                                </Container>
                            )}

                            {/* AI suggested documents */}
                            {suggestedDocuments.length > 0 && (
                                <Container
                                    style={{
                                        flexDirection: 'column',
                                        alignItems: 'stretch',
                                        gap: theme.spacing(1),
                                    }}
                                >
                                    <Container
                                        style={{
                                            alignItems: 'center',
                                            gap: theme.spacing(0.75),
                                        }}
                                    >
                                        <LoriIcon width={16} height={16} />
                                        <span
                                            style={{
                                                fontSize: 13,
                                                fontWeight: 600,
                                                color:
                                                    theme.palette.text.primary,
                                            }}
                                        >
                                            Suggested manuals &amp; warranty
                                            (Lori)
                                        </span>
                                    </Container>
                                    <Container
                                        style={{
                                            flexDirection: 'column',
                                            alignItems: 'stretch',
                                            gap: 0,
                                            borderRadius: 10,
                                            border: `1px solid ${theme.palette.warning.light}40`,
                                            backgroundColor: `${theme.palette.warning.light}08`,
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {suggestedDocuments.map(
                                            (
                                                doc: SuggestedDocument,
                                                index: number,
                                            ) => (
                                                <Container
                                                    key={doc.id}
                                                    style={{
                                                        justifyContent:
                                                            'space-between',
                                                        alignItems: 'center',
                                                        gap: theme.spacing(1),
                                                        padding: theme.spacing(
                                                            1.25,
                                                            2,
                                                        ),
                                                        borderBottom:
                                                            index <
                                                            suggestedDocuments.length -
                                                                1
                                                                ? `1px solid ${theme.palette.grey[100]}`
                                                                : 'none',
                                                    }}
                                                >
                                                    <Container
                                                        style={{
                                                            alignItems:
                                                                'center',
                                                            gap: theme.spacing(
                                                                1,
                                                            ),
                                                            flex: 1,
                                                            minWidth: 0,
                                                        }}
                                                    >
                                                        <DescriptionIcon
                                                            style={{
                                                                fontSize: 18,
                                                                color:
                                                                    theme
                                                                        .palette
                                                                        .warning
                                                                        .main,
                                                            }}
                                                        />
                                                        <span
                                                            style={{
                                                                fontSize: 13,
                                                                fontWeight: 500,
                                                                overflow:
                                                                    'hidden',
                                                                textOverflow:
                                                                    'ellipsis',
                                                                whiteSpace:
                                                                    'nowrap',
                                                            }}
                                                        >
                                                            {doc.title}
                                                        </span>
                                                        <Chip
                                                            size="small"
                                                            label={formatDocumentTypeLabel(
                                                                doc.document_type,
                                                            )}
                                                            style={{
                                                                height: 20,
                                                                fontSize: 10,
                                                                fontWeight: 500,
                                                                backgroundColor: `${theme.palette.warning.main}12`,
                                                                color:
                                                                    theme
                                                                        .palette
                                                                        .warning
                                                                        .dark,
                                                            }}
                                                        />
                                                        <Chip
                                                            size="small"
                                                            label={`${Math.round(
                                                                doc.confidence *
                                                                    100,
                                                            )}%`}
                                                            style={{
                                                                height: 18,
                                                                fontSize: 10,
                                                                fontWeight: 600,
                                                                backgroundColor:
                                                                    theme
                                                                        .palette
                                                                        .grey[100],
                                                                color:
                                                                    theme
                                                                        .palette
                                                                        .text
                                                                        .secondary,
                                                            }}
                                                        />
                                                    </Container>
                                                    <Container
                                                        style={{
                                                            gap: 4,
                                                            alignItems:
                                                                'center',
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        <Button
                                                            size="small"
                                                            startIcon={
                                                                <OpenInNewIcon
                                                                    style={{
                                                                        fontSize: 14,
                                                                    }}
                                                                />
                                                            }
                                                            onClick={() =>
                                                                window.open(
                                                                    doc.source_url,
                                                                    '_blank',
                                                                )
                                                            }
                                                            style={{
                                                                textTransform:
                                                                    'none',
                                                                fontSize: 12,
                                                                fontWeight: 500,
                                                                minWidth:
                                                                    'unset',
                                                                padding: theme.spacing(
                                                                    0.25,
                                                                    1,
                                                                ),
                                                            }}
                                                        >
                                                            Open
                                                        </Button>
                                                        <Tooltip title="Dismiss suggestion">
                                                            <IconButton
                                                                size="small"
                                                                disabled={
                                                                    dismissingId ===
                                                                    doc.id
                                                                }
                                                                onClick={() =>
                                                                    handleDismissSuggestion(
                                                                        doc.id,
                                                                    )
                                                                }
                                                                style={{
                                                                    padding: 4,
                                                                }}
                                                            >
                                                                {dismissingId ===
                                                                doc.id ? (
                                                                    <CircularProgress
                                                                        size={
                                                                            14
                                                                        }
                                                                    />
                                                                ) : (
                                                                    <CloseIcon
                                                                        style={{
                                                                            fontSize: 16,
                                                                            color:
                                                                                theme
                                                                                    .palette
                                                                                    .text
                                                                                    .disabled,
                                                                        }}
                                                                    />
                                                                )}
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Container>
                                                </Container>
                                            ),
                                        )}
                                    </Container>
                                    <span
                                        style={{
                                            fontSize: 11,
                                            color: theme.palette.text.disabled,
                                            fontStyle: 'italic',
                                        }}
                                    >
                                        Links are suggested by Lori; verify
                                        before relying on them.
                                    </span>
                                </Container>
                            )}

                            {/* Upload area */}
                            <Container
                                style={{
                                    padding: theme.spacing(2),
                                    borderRadius: 12,
                                    border: `1px solid ${theme.palette.grey[200]}`,
                                    backgroundColor: theme.palette.grey[50],
                                    flexDirection: 'column',
                                    gap: theme.spacing(1.5),
                                }}
                            >
                                <span
                                    style={{
                                        fontSize: 12,
                                        fontWeight: 600,
                                        color: theme.palette.text.secondary,
                                    }}
                                >
                                    Upload a document
                                </span>
                                <Container
                                    style={{
                                        gap: theme.spacing(1),
                                        alignItems: 'center',
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    <TextField
                                        size="small"
                                        variant="outlined"
                                        placeholder="Document name (optional)"
                                        value={documentName}
                                        onChange={(event) =>
                                            setDocumentName(event.target.value)
                                        }
                                        style={{
                                            flex: 1,
                                            minWidth: 180,
                                            backgroundColor: '#fff',
                                        }}
                                        InputProps={{
                                            style: { borderRadius: 8 },
                                        }}
                                    />
                                    <TextField
                                        select
                                        size="small"
                                        variant="outlined"
                                        value={documentType}
                                        onChange={(event) =>
                                            setDocumentType(
                                                event.target
                                                    .value as ProductDocumentType,
                                            )
                                        }
                                        style={{
                                            minWidth: 130,
                                            backgroundColor: '#fff',
                                        }}
                                        InputProps={{
                                            style: { borderRadius: 8 },
                                        }}
                                    >
                                        {DOCUMENT_TYPES.map((type) => (
                                            <MenuItem key={type} value={type}>
                                                {formatDocumentTypeLabel(type)}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        startIcon={
                                            uploading ? (
                                                <CircularProgress
                                                    size={14}
                                                    color="inherit"
                                                />
                                            ) : (
                                                <CloudUploadIcon />
                                            )
                                        }
                                        disabled={uploading}
                                        onClick={() =>
                                            fileInputRef.current?.click()
                                        }
                                        style={{
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            borderRadius: 8,
                                            boxShadow: 'none',
                                        }}
                                    >
                                        {uploading
                                            ? 'Uploading...'
                                            : 'Choose file'}
                                    </Button>
                                </Container>
                            </Container>
                        </Container>
                    </Container>
                )}
            </DialogContent>
            <DialogActions
                style={{
                    padding: theme.spacing(2, 3),
                    gap: theme.spacing(1),
                    borderTop: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Button
                    size="small"
                    disabled={!profile || deletingProfile}
                    onClick={handleDeleteProfile}
                    style={{
                        textTransform: 'none',
                        color: theme.palette.error.main,
                        fontWeight: 500,
                        borderRadius: 8,
                        border: `1px solid ${theme.palette.error.main}30`,
                        padding: theme.spacing(0.5, 1.5),
                    }}
                    startIcon={
                        deletingProfile ? (
                            <CircularProgress size={14} />
                        ) : (
                            <DeleteOutlineIcon style={{ fontSize: 16 }} />
                        )
                    }
                >
                    Delete product
                </Button>
                <span style={{ flex: 1 }} />
                <Button
                    onClick={onClose}
                    style={{
                        textTransform: 'none',
                        fontWeight: 500,
                        borderRadius: 8,
                    }}
                >
                    Close
                </Button>
            </DialogActions>

            <input
                ref={fileInputRef}
                type="file"
                hidden
                onChange={handleUpload}
            />
        </Dialog>
    )
}
